/**
 * The single door out of the browser.
 *
 * Every read and write goes through the Worker over this helper. There is no
 * database client in this bundle, and there is deliberately no key of any kind
 * shipped to the device - that is what makes the exposed-anon-key failure of the
 * system being replaced structurally impossible here rather than merely avoided.
 */
/**
 * Empty by default, and that default is load-bearing (17 Aug 2026).
 *
 * The PWA and the API are the SAME Worker on the same origin: it serves /health,
 * /session and /media/upload/... at the ROOT. There is no /api prefix anywhere in
 * the Worker.
 *
 * This defaulted to '/api' until now, and the deployed app worked only because
 * VITE_API_BASE happened to be an empty string at build time - `??` keeps '',
 * since '' is neither null nor undefined. A clean rebuild in a shell without
 * that variable would have folded '/api' in and pointed every call at /api/*,
 * where the production SPA fallback answers 200 with index.html. That failure
 * would not have looked like a wrong URL: res.ok is true, so the throw lands on
 * JSON.parse('<!doctype html>') and reads as the server returning corrupt data.
 * Confirmed against the deployed bundle, which ships base "".
 */
const BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

/**
 * The Worker answers errors two ways: `{ error: 'not_found' }` from the shell and
 * `{ error: { code: 'UNKNOWN_UNIT', ... } }` from every station route. Reading only
 * the first turned every station failure into the code `[object Object]`, which is
 * what `errorCopy` would then have shown an operator. Both shapes, one code.
 */
export function errorCodeOf(body: unknown): string {
  if (!body || typeof body !== 'object' || !('error' in body)) return 'unknown'
  const error = (body as { error: unknown }).error
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'code' in error) return String((error as { code: unknown }).code)
  return 'unknown'
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  })
  const text = await res.text()
  const body: unknown = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(res.status, errorCodeOf(body), `${res.status} ${errorCodeOf(body)}`)
  }
  return body as T
}
